import { lazy, Suspense, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { IconButton } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { columnIconName } from "@/lib/columnIcon";
import { StatusPill } from "@/components/ui/StatusPill";
import { DetailField } from "@/components/board/DetailField";
import { DetailColumnEditor } from "@/components/board/DetailColumnEditor";
import { MemoOriginBreadcrumb } from "@/components/board/MemoOriginBreadcrumb";
import { RowActionBar } from "@/components/board/RowActionBar";
import { RowSubtasks } from "@/components/board/RowSubtasks";
import { TicketLinksPanel } from "@/components/board/TicketLinksPanel";
import { ShareRowDialog } from "@/components/board/ShareRowDialog";
// Lazy: pulls in TipTap + react-markdown (~600 KB), only needed once a row
// detail opens — keeps the initial board paint light.
const RowComments = lazy(() =>
  import("@/components/board/RowComments").then((m) => ({ default: m.RowComments })),
);
import { useAuth } from "@/hooks/useAuth";
import { useRowShares } from "@/hooks/useRowShares";
import { useRowSubscription } from "@/hooks/useRowSubscription";
import { useMarkRowSeen } from "@/hooks/useMarkRowSeen";
import { useBoardPeople } from "@/hooks/useBoardPeople";
import { useRowMentionPeople } from "@/hooks/useRowMentionPeople";
import { usePersonalWorkspaceId } from "@/hooks/useWorkspaceMeta";
import { rowMentionItems } from "@/lib/boardPeople";
import type { MentionItem } from "@/lib/types";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import { BRAND } from "@/lib/tokens";
import { relPast, ticketAge, ticketAgeColor } from "@/lib/format";
import type { CellValue, UUID } from "@/lib/types";
import { isTicketBoard, ticketLabel, type ViewBoard, type ViewRow } from "@/lib/boardView";

function rowRelTime(iso: string, label: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff) || diff < 0) return label;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return `${label} gerade eben`;
  if (min < 60) return `${label} vor ${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${label} vor ${h} Std.`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${label} vor ${d} ${d === 1 ? "Tag" : "Tagen"}`;
  return `${label} ${new Date(iso).toLocaleDateString("de-DE", { day: "numeric", month: "short" })}`;
}

async function copyRowLink(rowId: string) {
  const url = `${window.location.origin}${window.location.pathname}?row=${rowId}`;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }
    showToast(t.copyLinkSuccess, "success");
  } catch {
    showToast(t.copyLinkFail, "error");
  }
}

interface RowDetailPanelProps {
  row: ViewRow;
  board: ViewBoard;
  onClose: () => void;
  onPatch: (patch: Partial<ViewRow>) => void;
  /** Per-column raw cell write — lets the panel render + edit EVERY board column
   * (not just the canonical flat ViewRow fields). Without it, columns are
   * read-only in the panel. */
  onPatchCell?: (rowId: string, columnId: string, value: CellValue) => void;
  /** Unteraufgaben: mint a child row and write its title + assignee in one step. */
  onCreateSubtask?: (parentRowId: UUID, title: string, assigneeId: string | null) => void;
  /** Unteraufgaben: open a sub-task's own detail ("öffnen"). */
  onOpenRow?: (rowId: string) => void;
  /** Soft-delete this row (undoable). Omitted for guests / fixture mode. */
  onDelete?: () => void;
  /** Duplicate this row, optionally with its comments. */
  onDuplicate?: (withComments: boolean) => void;
  /** Presentation (⑤): "docked" fills its parent pane (parent sets the width via a
   * draggable splitter); "overlay" floats over the board on the right with a dimming
   * backdrop; "centered" is a large centered modal (portaled). Default "overlay". */
  variant?: "docked" | "overlay" | "centered";
  /** When set, the header shows an expand/collapse control that fires this to switch
   * between docked and the large centered view. Omit to hide it (mobile / fixtures). */
  onToggleLarge?: () => void;
  /** Jump-to-comment (①): the comment to scroll to + flash inside the thread. */
  focusCommentId?: string;
  focusCommentQuery?: string;
}

export function RowDetailPanel({
  row,
  board,
  onClose,
  onPatch,
  onPatchCell,
  onCreateSubtask,
  onOpenRow,
  onDelete,
  onDuplicate,
  variant = "overlay",
  onToggleLarge,
  focusCommentId,
  focusCommentQuery,
}: RowDetailPanelProps) {
  const docked = variant === "docked";
  const centered = variant === "centered";
  const statusOpt = board.columns
    .find((c) => c.type === "status")
    ?.options?.find((o) => o.id === row.status);
  // The first text column is the row title (panel header). EVERY other column —
  // including long_text ("Beschreibung"/"Notizen"/…) — renders as a field under
  // its REAL name, editable, via DetailColumnEditor. No special-cased Notizen
  // block (it died into a dead, uneditable box on boards with no long_text
  // column); free text is just a long_text column, or use comments.
  const titleColId = board.columns.find((c) => c.type === "text")?.id;
  const fieldColumns = board.columns.filter((c) => c.id !== titleColId);

  // Whole-board people (members ∪ guests) resolve the description-editor's name below.
  const { data: people = [] } = useBoardPeople(board.id as UUID);
  // The @-mention pool is ROW-SCOPED: board members ∪ the row's creator ∪ everyone the
  // row is shared with (Phase 4 row_shares). So a per-ticket shared-in colleague can be
  // tagged (and can tag members back) — the whole-board people list would miss them, and
  // the old members-∪-creator filter dropped them. See useRowMentionPeople / rowMentionItems.
  const { data: mentionPeople = [] } = useRowMentionPeople(row.raw?.id as UUID | undefined);
  const mentionItems = useMemo<MentionItem[]>(
    () => rowMentionItems(mentionPeople),
    [mentionPeople],
  );
  const ticketing = isTicketBoard(board);
  // Real ticket ID (IT-42) on ticket boards; the synthetic r042 elsewhere.
  const idLabel =
    (ticketing && ticketLabel(board.ticketPrefix, row.ticketNumber)) ||
    `r${row.id
      .replace(/[^0-9]/g, "")
      .padStart(3, "0")
      .slice(-3)}`;
  // Ticket lifetime/age chip (open: growing+aging; resolved: frozen).
  const age = ticketing && row.raw ? ticketAge(row.raw.created_at, row.raw.resolved_at) : null;
  const sub = useRowSubscription(row.raw?.id as UUID | undefined);
  useMarkRowSeen(row.raw?.id as UUID | undefined);
  // Teilen (share this ticket): board staff (non-guest) OR the ticket's creator.
  const { user } = useAuth();
  const [shareOpen, setShareOpen] = useState(false);
  const viewerIsCreator =
    Boolean(user?.sub) &&
    row.raw?.created_by_keycloak_sub != null &&
    user?.sub === row.raw.created_by_keycloak_sub;
  const personalWsId = usePersonalWorkspaceId();
  // Personal boards are private — they can't be shared.
  const canShare =
    board.workspace_id !== personalWsId && (board.access !== "guest" || viewerIsCreator);

  // The requester's Beschreibung (first long_text column) on a ticket board is
  // the problem statement. Staff working the ticket see it read-only; only the
  // creator or an admin may rewrite it. The real lock is the cell-write route;
  // this is the matching UI gate plus a "bearbeitet von" note when it was edited.
  const descriptionColId = ticketing
    ? (board.columns.find((c) => c.type === "long_text")?.id ?? null)
    : null;
  const canEditDescription = viewerIsCreator || board.canAdmin;
  const descriptionCell =
    descriptionColId != null
      ? (row.raw?.cells?.find((c) => c.column_def_id === descriptionColId) ?? null)
      : null;
  // "Edited after submission": the cell's updated_at drifts past the row's
  // creation by more than a short grace once someone changes it. On plain submit
  // the two are within the same transaction, so no note shows.
  const descriptionEdit = (() => {
    if (!descriptionCell || !row.raw?.created_at) return null;
    const created = new Date(row.raw.created_at).getTime();
    const updated = new Date(descriptionCell.updated_at).getTime();
    if (Number.isNaN(created) || Number.isNaN(updated) || updated - created < 5000) return null;
    const editor = people.find((p) => p.user_id === descriptionCell.updated_by);
    return {
      name: editor?.display_name ?? editor?.username ?? editor?.email ?? null,
      at: descriptionCell.updated_at,
    };
  })();
  // "Geteilt mit" summary — proactive shared-state indicator for staff/creator.
  const rowShares = useRowShares(row.raw?.id as UUID | undefined, canShare && Boolean(row.raw));
  const shareTargets = [
    ...rowShares.shares.users.map((u) => u.display_name ?? u.username ?? u.email),
    ...rowShares.shares.departments.map((d) => d.name),
  ];
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleValue, setTitleValue] = useState(row.title);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  useEffect(() => {
    // Don't overwrite an in-progress rename: only re-seed from the row when not
    // actively editing (a concurrent external rename / poll would clobber the draft).
    if (!titleEditing) setTitleValue(row.title);
  }, [row.id, row.title, titleEditing]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const commitTitle = () => {
    setTitleEditing(false);
    if (titleValue !== row.title) onPatch({ title: titleValue });
  };

  // docked = fills its parent pane (parent sets the width via the splitter); centered =
  // a large centered modal (portaled below); overlay = floats over the board on the right
  // with a dimming backdrop.
  const FONT = "Inter Variable, Inter, system-ui, sans-serif";
  const asideStyle: CSSProperties = docked
    ? {
        flex: 1,
        minWidth: 0,
        height: "100%",
        background: "var(--surface)",
        borderLeft: "1px solid var(--hairline-strong)",
        display: "flex",
        flexDirection: "column",
        fontFamily: FONT,
      }
    : centered
      ? {
          width: 1320,
          maxWidth: "96vw",
          height: "94vh",
          maxHeight: "94vh",
          background: "var(--surface)",
          border: "1px solid var(--hairline-strong)",
          borderRadius: 14,
          boxShadow: "0 24px 64px rgba(13,62,99,0.28)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "flow-fade-in .16s ease-out",
          fontFamily: FONT,
        }
      : {
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: 540,
          maxWidth: "94%",
          background: "var(--surface)",
          borderLeft: "1px solid var(--hairline-strong)",
          boxShadow: "-12px 0 30px rgba(13,62,99,0.12)",
          display: "flex",
          flexDirection: "column",
          zIndex: 41,
          animation: "flow-slide-right-in .22s ease-out",
          fontFamily: FONT,
        };
  const body = (
    <aside
      data-detail-variant={variant}
      style={asideStyle}
      onClick={centered ? (e) => e.stopPropagation() : undefined}
    >
      <div
        style={{
          padding: "12px 14px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          borderBottom: "1px solid var(--hairline-soft)",
        }}
      >
        {statusOpt ? <StatusPill option={statusOpt} size="sm" /> : null}
        <span
          style={{
            fontSize: 12,
            color: "var(--ink-3)",
            fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
          }}
        >
          {idLabel}
        </span>
        {age ? (
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              color: ticketAgeColor(age.tone),
              background: "var(--surface-soft)",
              borderRadius: 999,
              padding: "2px 9px",
            }}
          >
            {age.label}
          </span>
        ) : null}
        <div style={{ flex: 1 }}></div>
        {row.raw ? (
          <IconButton
            icon="bell"
            size={30}
            title={sub.subscribed ? t.unfollowAria : t.followAria}
            color={sub.subscribed ? BRAND.orange : undefined}
            disabled={sub.busy}
            onClick={() => sub.toggle.mutate(!sub.subscribed)}
          />
        ) : null}
        {row.raw && canShare ? (
          <IconButton icon="share" size={30} title={t.share} onClick={() => setShareOpen(true)} />
        ) : null}
        <IconButton
          icon="copy"
          size={30}
          title={t.copyLink}
          onClick={() => void copyRowLink(row.id)}
        />
        <IconButton
          icon="archive"
          size={30}
          title={t.comingSoon}
          onClick={() => showToast(t.comingSoon)}
        />
        <div ref={menuRef} style={{ position: "relative" }}>
          <IconButton
            icon="dotsV"
            size={30}
            title={onDuplicate ? t.moreActions : t.comingSoon}
            active={menuOpen}
            onClick={() => (onDuplicate ? setMenuOpen((v) => !v) : showToast(t.comingSoon))}
          />
          {menuOpen && onDuplicate ? (
            <div
              role="menu"
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                right: 0,
                minWidth: 220,
                background: "var(--surface-elev)",
                border: "1px solid var(--hairline-strong)",
                borderRadius: 10,
                boxShadow: "0 10px 28px rgba(13,62,99,0.18)",
                padding: 5,
                zIndex: 50,
              }}
            >
              {[
                { label: t.duplicate, withComments: false },
                { label: t.duplicateWithComments, withComments: true },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onDuplicate(item.withComments);
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "8px 10px",
                    border: "none",
                    background: "transparent",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 13,
                    color: "var(--ink-1)",
                    textAlign: "left",
                    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                  }}
                >
                  <Icon name="copy" size={14} color="var(--ink-3)" />
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {onDelete ? (
          <IconButton
            icon="trash"
            size={30}
            title={t.delete}
            color="var(--danger)"
            onClick={onDelete}
          />
        ) : null}
        <div style={{ width: 1, height: 18, background: "var(--hairline)", margin: "0 4px" }}></div>
        {onToggleLarge ? (
          <IconButton
            icon={centered ? "minimize" : "maximize"}
            size={30}
            onClick={onToggleLarge}
            title={centered ? t.detailCollapse : t.detailExpand}
            aria-label={centered ? t.detailCollapse : t.detailExpand}
          />
        ) : null}
        <IconButton icon="x" size={30} onClick={onClose} title={t.closeEsc} />
      </div>

      {/* In the large centered view, frame the content in a ~1080px reading column
          (centered via symmetric side padding, clamped to 28px) — clearly wider than the
          old ~844px modal content so it reads as genuinely bigger, while still not
          full-bleed 1320px. Tune this one number to taste. Docked/overlay unchanged. */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: centered ? "22px max(28px, (100% - 1080px) / 2) 60px" : "22px 28px 60px",
        }}
      >
        {titleEditing ? (
          <input
            autoFocus
            name="row-title"
            autoComplete="off"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitTitle();
              if (e.key === "Escape") {
                e.stopPropagation();
                setTitleValue(row.title);
                setTitleEditing(false);
              }
            }}
            style={{
              width: "100%",
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.015em",
              lineHeight: 1.25,
              color: "var(--ink-1)",
              fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              border: "none",
              outline: "none",
              background: "transparent",
              padding: 0,
              margin: "0 0 14px",
            }}
          />
        ) : (
          <h2
            ref={titleRef}
            onClick={() => setTitleEditing(true)}
            style={{
              margin: "0 0 14px",
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.015em",
              color: "var(--ink-1)",
              lineHeight: 1.25,
              cursor: "text",
            }}
          >
            {row.title}
          </h2>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 12,
            color: "var(--ink-3)",
            marginBottom: 22,
            flexWrap: "wrap",
          }}
        >
          {row.raw?.created_by_display_name ? (
            <span>
              {t.createdBy}{" "}
              <strong style={{ color: "var(--ink-1)", fontWeight: 600 }}>
                {row.raw.created_by_display_name}
              </strong>
            </span>
          ) : null}
          {row.raw?.created_at ? (
            <>
              <span style={{ width: 3, height: 3, borderRadius: 99, background: "currentColor" }} />
              <span>{rowRelTime(row.raw.created_at, t.createdLabel)}</span>
            </>
          ) : null}
          {row.raw?.updated_at && row.raw.updated_at !== row.raw.created_at ? (
            <>
              <span style={{ width: 3, height: 3, borderRadius: 99, background: "currentColor" }} />
              <span>{rowRelTime(row.raw.updated_at, t.updatedLabel)}</span>
            </>
          ) : null}
        </div>

        <MemoOriginBreadcrumb board={board} row={row} />

        {shareTargets.length > 0 ? (
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            title={t.share}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              marginBottom: 22,
              padding: "8px 11px",
              background: "var(--surface-soft)",
              border: "1px solid var(--hairline-soft)",
              borderRadius: 10,
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            }}
          >
            <Icon name="share" size={14} color="var(--accent)" />
            <span style={{ fontSize: 12, color: "var(--ink-3)", flexShrink: 0 }}>
              {t.sharedWithLabel}
            </span>
            <span
              style={{
                fontSize: 12.5,
                color: "var(--ink-1)",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {shareTargets.join(", ")}
            </span>
          </button>
        ) : null}

        {fieldColumns.length > 0 ? (
          <div
            style={{
              background: "var(--surface-soft)",
              borderRadius: 12,
              border: "1px solid var(--hairline-soft)",
            }}
          >
            {fieldColumns.map((col, i) => (
              <DetailField
                key={col.id}
                icon={columnIconName(col.type)}
                label={col.name}
                last={i === fieldColumns.length - 1}
              >
                {/* Keyed on the ROW: a field editor holds local edit state (an open
                    Beschreibung editor + its draft). Without the key it survived a row
                    switch and showed ticket A's text on ticket B — and a blur would have
                    saved it there. Same class as the RowComments `key={rowId}` fix. */}
                <DetailColumnEditor
                  key={row.id}
                  column={col}
                  row={row}
                  board={board}
                  onPatchCell={onPatchCell}
                  mentionItems={mentionItems}
                  readOnly={col.id === descriptionColId && !canEditDescription}
                />
                {col.id === descriptionColId && descriptionEdit ? (
                  <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--ink-4)" }}>
                    {descriptionEdit.name
                      ? `Bearbeitet von ${descriptionEdit.name} · ${relPast(descriptionEdit.at)}`
                      : `Bearbeitet · ${relPast(descriptionEdit.at)}`}
                  </div>
                ) : null}
              </DetailField>
            ))}
          </div>
        ) : null}

        {/* Unteraufgaben — Aufgaben boards only (the gate lives in RowSubtasks, which the
              mobile sheet renders too, so the two surfaces cannot drift apart). */}
        <RowSubtasks
          board={board}
          row={row}
          onPatchCell={onPatchCell}
          onCreateSubtask={onCreateSubtask}
          onOpenRow={onOpenRow}
        />

        {row.raw?.id ? (
          <>
            {ticketing ? <TicketLinksPanel row={row} board={board} /> : null}
            <RowActionBar row={row} board={board} />
            <div style={{ marginTop: 22 }}>
              <Suspense fallback={<div style={{ minHeight: 80 }} />}>
                <RowComments
                  // Key on the row so switching rows remounts the composer — an
                  // unsent draft must NOT follow you into the next row (it was being
                  // posted to the wrong row by mistake).
                  key={row.raw.id as UUID}
                  rowId={row.raw.id as UUID}
                  mentionItems={mentionItems}
                  isTicket={ticketing}
                  focusCommentId={focusCommentId}
                  focusCommentQuery={focusCommentQuery}
                />
              </Suspense>
            </div>
          </>
        ) : null}
      </div>
    </aside>
  );
  const dialogs =
    row.raw && shareOpen ? (
      <ShareRowDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        rowId={row.raw.id as UUID}
        homeWorkspaceId={board.workspace_id as UUID}
      />
    ) : null;
  if (centered) {
    // Portal to body so the centered modal escapes any transformed/positioned ancestor
    // (the board's relative split container). Backdrop click closes; the aside stops it.
    return (
      <>
        {createPortal(
          <div
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
              background: "rgba(13,62,99,0.30)",
              zIndex: 120,
              animation: "flow-fade-in .15s ease-out",
            }}
          >
            {body}
          </div>,
          document.body,
        )}
        {dialogs}
      </>
    );
  }
  return (
    <>
      {docked ? null : (
        <div
          onClick={onClose}
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(13,62,99,0.18)",
            zIndex: 40,
            animation: "flow-fade-in .15s ease-out",
          }}
        ></div>
      )}
      {body}
      {dialogs}
    </>
  );
}
